import React from 'react';
import type { ShorthandValue } from '@archipro-design/aria';
import {
    DropdownProduct,
    Grid,
    pxToRem,
    useStyles,
} from '@archipro-design/aria';
import * as S from './OptionPicker.style';
import { useVariantOptionState } from '@modules/product/context/VariantOptionContext';
import { useVariantDecision } from '@modules/product/hook/use-variant-decision';
import { SortDown } from '@archipro-design/icons';
import type ProductSize from '@modules/product/type/product-size';
import type { BasePickerProps } from '@modules/product/component/variant-selector/OptionPicker';
import OptionLabelWithTooltip from './OptionLabelWithTooltip';
import type { ButtonProps } from '@fluentui/react-northstar';
import { useTracker } from '@archipro-website/tracker';

type SizePickerProps = BasePickerProps & {
    data: {
        sizes: ProductSize[];
    };
};

interface DropdownItemType {
    header: string;
    key: number;
}

const emptyOption = {
    key: 0,
    header: 'Not Applicable',
};

const SizePicker = ({
    data: { variants, sizes },
    isGeneric,
}: SizePickerProps) => {
    const { decision } = useVariantOptionState();
    const { pickSize } = useVariantDecision(variants);
    const [menuOpen, setMenuOpen] = React.useState(false);
    const { css } = useStyles<{ menuOpen: boolean }>({ menuOpen });
    const tracker = useTracker();

    const items = sizes.map((size) => ({
        header: size.FullTitle,
        key: size.ID,
    }));

    const option =
        items.find((opt) => opt.key === decision.size) ?? emptyOption;

    if (!isGeneric) {
        items.unshift(emptyOption);
    }

    const onOptionChange = (value: ShorthandValue<DropdownItemType>) => {
        const key = value ? (value as DropdownItemType).key : 0;
        tracker.log('DropDownSelect', {
            url: new URL(window.location.href),
            targetTracker: 'archiproTracker',
            data: {
                ExtraData: key,
            },
        });
        pickSize(key);
    };

    return (
        <Grid columns={`${pxToRem(394)} 1fr`} className={css(S.OptionPicker)}>
            <label className={css(S.OptionLabel)}>Dimensions</label>
            {items.length > 1 ? (
                <DropdownProduct
                    className={css(S.OptionDropdown)}
                    value={option}
                    placeholder={emptyOption.header}
                    items={items}
                    onChange={(_, { value }) =>
                        onOptionChange(value as DropdownItemType)
                    }
                    toggleIndicator={<SortDown />}
                    onOpenChange={(_, { open }) => setMenuOpen(Boolean(open))}
                    triggerButton={
                        {
                            children: (
                                TriggerButton: React.ElementType<ButtonProps>,
                                props: ButtonProps,
                            ) => {
                                return (
                                    <TriggerButton
                                        {...props}
                                        content={undefined}
                                    >
                                        <OptionLabelWithTooltip
                                            label={option.header}
                                        />
                                    </TriggerButton>
                                );
                            },
                        } as ShorthandValue<ButtonProps>
                    }
                    variables={{
                        belowListBorderRadius: 'unset',
                    }}
                />
            ) : (
                <div className={css(S.SingleValue)}>
                    <OptionLabelWithTooltip label={option.header} />
                </div>
            )}
        </Grid>
    );
};

export default SizePicker;
